import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { createPortal } from 'react-dom';
import { Heart, X, MapPin, Maximize2, Trash2, Zap, Trophy, Loader2, Share2, Check, TrendingUp, Award, Sparkles, CheckCircle2 } from 'lucide-react';
import { useFavorites } from '../hooks/useFavorites';
import { parsePriceToRupees, parseAreaToSqft, formatRupees, pricePerSqft } from '../utils/parsePriceArea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- HT status pill ---------------------------------------------------------
const HT_TONE = {
  clear:    { cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'HT clear' },
  info:     { cls: 'bg-sky-100 text-sky-800 border-sky-200',             label: 'HT nearby' },
  warning:  { cls: 'bg-amber-100 text-amber-800 border-amber-200',       label: 'HT close' },
  critical: { cls: 'bg-rose-100 text-rose-800 border-rose-200',          label: 'HT over land' },
  unknown:  { cls: 'bg-slate-100 text-slate-600 border-slate-200',       label: '—' },
};

/** Floating heart pill + side-by-side compare drawer for saved plots.
 *  Purely client-side (localStorage) — no auth required. */
export const FavoritesFab = () => {
  const { favIds, count, toggle, clear, mergeIn } = useFavorites();
  const [open, setOpen] = useState(false);
  const [properties, setProperties] = useState({});
  const [loading, setLoading] = useState(false);
  const [htStatus, setHtStatus] = useState({}); // { [propId]: 'clear'|'info'|'warning'|'critical'|'unknown' }
  const [htLoading, setHtLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  // Global "open favourites" hook — the "View my saved plots" button in the
  // Projects section (and any other CTA) dispatches this so we can pop the
  // compare modal without prop-drilling.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('am-open-favourites', handler);
    return () => window.removeEventListener('am-open-favourites', handler);
  }, []);

  // Read `?fav=id1,id2,id3` from the URL on first mount → merge into user's
  // favourites and auto-open the compare modal so a shared link "just works".
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('fav');
    if (!raw) return;
    const ids = raw.split(',').map((s) => s.trim()).filter(Boolean);
    if (ids.length === 0) return;
    mergeIn(ids);
    setOpen(true);
    // Strip the query param so a refresh doesn't re-open the modal every time
    params.delete('fav');
    const url = window.location.pathname + (params.toString() ? `?${params}` : '') + window.location.hash;
    window.history.replaceState({}, '', url);
  }, []);

  // Global "open favourites" hook — the "View my saved plots" button in the
  // Projects section (and any other CTA) dispatches this so we can pop the
  // compare modal without prop-drilling.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('am-open-favourites', handler);
    return () => window.removeEventListener('am-open-favourites', handler);
  }, []);

  useEffect(() => {
    if (!open || favIds.length === 0) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/properties`)
      .then(res => {
        if (cancelled) return;
        const items = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.properties || []);
        const map = {};
        items.forEach(p => { map[p.id] = p; });
        setProperties(map);
      })
      .catch(() => setProperties({}))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, favIds]);

  // Fetch HT-scan verdicts for each favourite in parallel (cached server-side,
  // so this is instant on repeat opens).
  useEffect(() => {
    if (!open) return;
    const ids = favIds.filter(id => properties[id] && !htStatus[id]);
    if (ids.length === 0) return;
    let cancelled = false;
    setHtLoading(true);
    Promise.all(
      ids.map(id =>
        axios
          .get(`${API}/properties/${id}/ht-wire-report`)
          .then(r => [id, r.data?.checked ? (r.data.verdict || 'unknown') : 'unknown'])
          .catch(() => [id, 'unknown']),
      ),
    )
      .then(results => {
        if (cancelled) return;
        setHtStatus(prev => {
          const next = { ...prev };
          results.forEach(([id, v]) => { next[id] = v; });
          return next;
        });
      })
      .finally(() => !cancelled && setHtLoading(false));
    return () => { cancelled = true; };
  }, [open, properties]);  // htStatus intentionally omitted — only fetch on new opens

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (count === 0 && !open) return null;

  const saved = favIds.map(id => properties[id]).filter(Boolean);

  return (
    <>
      {count > 0 && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="am-fav-fab"
          data-testid="favorites-fab"
          aria-label={`Compare ${count} saved plot${count > 1 ? 's' : ''}`}
          title={`Compare ${count} saved plot${count > 1 ? 's' : ''}`}
        >
          <Heart size={14} fill="currentColor" />
          <span>Favourites</span>
          <span className="am-fav-fab-count">{count}</span>
        </button>
      )}

      {open && createPortal(
        <div
          className="am-fav-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid="favorites-modal"
        >
          <div className="am-fav-drawer" onMouseDown={(e) => e.stopPropagation()}>
            <div className="am-fav-drawer-top">
              <div className="min-w-0">
                <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
                  <Heart size={16} className="text-rose-600" fill="currentColor" />
                  Compare your favourites
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Saved locally in your browser — nothing sent to our servers.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {count > 0 && (
                  <button
                    onClick={async () => {
                      const url = `${window.location.origin}${window.location.pathname}?fav=${favIds.join(',')}`;
                      try {
                        if (navigator.share) {
                          await navigator.share({
                            title: 'My shortlisted plots on Acre Mitra',
                            text: `Compare these ${count} plots with me:`,
                            url,
                          });
                        } else {
                          await navigator.clipboard.writeText(url);
                          setShareCopied(true);
                          setTimeout(() => setShareCopied(false), 2400);
                        }
                      } catch { /* user cancelled share */ }
                    }}
                    className="text-[10px] font-bold text-emerald-800 hover:text-emerald-900 inline-flex items-center gap-1 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 rounded-full px-2 py-1 transition-colors"
                    data-testid="favorites-share"
                    title="Copy a shareable link"
                  >
                    {shareCopied
                      ? (<><Check size={10} /> Link copied</>)
                      : (<><Share2 size={10} /> Share link</>)}
                  </button>
                )}
                {count > 0 && (
                  <button
                    onClick={clear}
                    className="text-[10px] font-bold text-rose-700 hover:text-rose-900 inline-flex items-center gap-1 border border-rose-200 hover:bg-rose-50 rounded-full px-2 py-1 transition-colors"
                    data-testid="favorites-clear"
                  >
                    <Trash2 size={10} /> Clear all
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="am-property-modal-close"
                  aria-label="Close favourites"
                  data-testid="favorites-close"
                >
                  <X size={16} />
                  <span className="hidden sm:inline">Close</span>
                </button>
              </div>
            </div>

            <div className="am-fav-drawer-body">
              {loading && <div className="text-xs text-slate-500">Loading your saved plots…</div>}
              {!loading && saved.length === 0 && (
                <div className="text-center text-slate-500 py-8">
                  <Heart size={40} className="mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">No favourites yet. Tap the heart on any plot to save it.</p>
                </div>
              )}
              {!loading && saved.length > 0 && (
                <div className="am-fav-grid">
                  {saved.map((p) => {
                    const img = p.image?.match(/^\/api\/(uploads|files)\//) ? `${BACKEND_URL}${p.image}` : p.image;
                    return (
                      <div key={p.id} className="am-fav-card" data-testid={`favorite-card-${p.id}`}>
                        <div className="am-fav-card-img">
                          <img
                            src={img}
                            alt={p.title}
                            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=800&q=80'; }}
                          />
                          <button
                            onClick={() => toggle(p.id)}
                            className="am-fav-card-remove"
                            aria-label="Remove from favourites"
                            data-testid={`favorite-remove-${p.id}`}
                            title="Remove"
                          >
                            <X size={12} />
                          </button>
                        </div>
                        <div className="am-fav-card-body">
                          <div className="am-fav-card-title">{p.title}</div>
                          <div className="am-fav-card-loc">
                            <MapPin size={11} /> {p.location}
                          </div>
                          <div className="am-fav-card-row">
                            <span><Maximize2 size={11} /> {p.area}</span>
                            <span className="am-fav-card-price">{p.price}</span>
                          </div>
                          {p.property_code && (
                            <a
                              href={`/property/${p.property_code}`}
                              className="am-fav-card-cta"
                              data-testid={`favorite-view-${p.id}`}
                            >
                              View details
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* --- 🏆 Best Pick + 10-year projection --- */}
              {!loading && saved.length >= 2 && (
                <BestPickCard saved={saved} htStatus={htStatus} />
              )}

              {/* --- Side-by-side diff table --- */}
              {!loading && saved.length >= 2 && (
                <FavoritesDiffTable
                  saved={saved}
                  htStatus={htStatus}
                  htLoading={htLoading}
                />
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default FavoritesFab;

// ---------------------------------------------------------------------------
// Side-by-side comparison table below the cards. Highlights the winner in
// each numeric column (cheapest price, lowest ₹/sqft, largest area, cleanest
// HT status) so buyers can pick at a glance.
// ---------------------------------------------------------------------------
const HT_RANK = { clear: 0, info: 1, warning: 2, critical: 3, unknown: 4 };

const FavoritesDiffTable = ({ saved, htStatus, htLoading }) => {
  const rows = saved.map(p => ({
    p,
    priceNum: parsePriceToRupees(p.price),
    areaSqft: parseAreaToSqft(p.area),
    perSqft: pricePerSqft(p.price, p.area),
    ht: htStatus[p.id] || 'unknown',
  }));

  // Winner detection (min for price/perSqft/ht rank; max for area).
  const finite = (xs) => xs.filter(x => Number.isFinite(x));
  const minOr = (xs, fb) => (finite(xs).length ? Math.min(...finite(xs)) : fb);
  const maxOr = (xs, fb) => (finite(xs).length ? Math.max(...finite(xs)) : fb);

  const minPrice   = minOr(rows.map(r => r.priceNum), null);
  const minPerSqft = minOr(rows.map(r => r.perSqft), null);
  const maxArea    = maxOr(rows.map(r => r.areaSqft), null);
  const bestHtRank = Math.min(...rows.map(r => HT_RANK[r.ht] ?? 4));

  return (
    <div className="am-fav-diff-wrap" data-testid="favorites-diff-table">
      <div className="flex items-center gap-2 mb-2">
        <Trophy size={13} className="text-amber-600" />
        <h4 className="text-xs font-black uppercase tracking-widest text-slate-700">
          Side-by-side comparison
        </h4>
        {htLoading && (
          <span className="text-[10px] text-slate-500 inline-flex items-center gap-1 ml-auto">
            <Loader2 size={10} className="animate-spin" /> Loading HT status…
          </span>
        )}
      </div>

      <div className="am-fav-diff-scroll">
        <table className="am-fav-diff-table">
          <thead>
            <tr>
              <th className="text-left">Metric</th>
              {rows.map(({ p }) => (
                <th key={p.id} className="text-left">
                  <div className="am-fav-diff-th">
                    <span className="am-fav-diff-th-title">{p.title}</span>
                    <span className="am-fav-diff-th-loc">{p.location}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="am-fav-diff-label">Price</td>
              {rows.map(({ p, priceNum }) => (
                <td key={p.id}>
                  <span className={priceNum != null && priceNum === minPrice ? 'am-fav-diff-win' : ''}>
                    {p.price || '—'}
                  </span>
                  {priceNum != null && priceNum === minPrice && (
                    <span className="am-fav-diff-badge" data-testid={`fav-diff-cheapest-${p.id}`}>
                      Cheapest
                    </span>
                  )}
                </td>
              ))}
            </tr>
            <tr>
              <td className="am-fav-diff-label">Area</td>
              {rows.map(({ p, areaSqft }) => (
                <td key={p.id}>
                  <span className={areaSqft != null && areaSqft === maxArea ? 'am-fav-diff-win' : ''}>
                    {p.area || '—'}
                  </span>
                  {areaSqft && (
                    <span className="am-fav-diff-sub">≈ {areaSqft.toLocaleString('en-IN')} sqft</span>
                  )}
                  {areaSqft != null && areaSqft === maxArea && (
                    <span className="am-fav-diff-badge" data-testid={`fav-diff-largest-${p.id}`}>
                      Largest
                    </span>
                  )}
                </td>
              ))}
            </tr>
            <tr>
              <td className="am-fav-diff-label">₹ / sqft</td>
              {rows.map(({ p, perSqft }) => (
                <td key={p.id}>
                  <span className={perSqft != null && perSqft === minPerSqft ? 'am-fav-diff-win' : ''}>
                    {perSqft ? formatRupees(perSqft) : '—'}
                  </span>
                  {perSqft != null && perSqft === minPerSqft && (
                    <span className="am-fav-diff-badge" data-testid={`fav-diff-best-value-${p.id}`}>
                      Best value
                    </span>
                  )}
                </td>
              ))}
            </tr>
            <tr>
              <td className="am-fav-diff-label">
                <span className="inline-flex items-center gap-1">
                  <Zap size={11} className="text-amber-600" /> HT status
                </span>
              </td>
              {rows.map(({ p, ht }) => {
                const tone = HT_TONE[ht] || HT_TONE.unknown;
                const isBest = (HT_RANK[ht] ?? 4) === bestHtRank;
                return (
                  <td key={p.id}>
                    <span
                      className={`am-fav-diff-pill ${tone.cls}`}
                      data-testid={`fav-diff-ht-${p.id}`}
                    >
                      {tone.label}
                    </span>
                    {isBest && bestHtRank <= 1 && (
                      <span className="am-fav-diff-badge" data-testid={`fav-diff-safest-${p.id}`}>
                        Safest
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      <p className="am-fav-diff-note">
        <Trophy size={10} className="inline mr-1 text-amber-500" />
        Green highlights = winner in that row. HT status uses the same 2 km OSM
        scan shown on each property page — plots with an “HT clear” verdict are
        typically buildable without transmission-corridor complications.
      </p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// BestPickCard: scores every favourite on 6 dimensions (price, ₹/sqft, area,
// HT status, amenities count, deal urgency) and highlights the overall winner
// with 3 data-backed reasons + a 10-year profit projection using a 9% CAGR
// (typical Kolkata suburb residential land historical average).
// ---------------------------------------------------------------------------
const CAGR = 0.09;
const HORIZON_YEARS = 10;

const buildScore = (row, ctx) => {
  let s = 0;
  const notes = [];
  if (row.perSqft != null && row.perSqft === ctx.minPerSqft) {
    s += 30;
    const gap = ctx.runnerUpPerSqft && ctx.runnerUpPerSqft > row.perSqft
      ? Math.round(((ctx.runnerUpPerSqft - row.perSqft) / ctx.runnerUpPerSqft) * 100)
      : null;
    notes.push({
      icon: 'value',
      text: gap
        ? `Lowest ₹/sqft in your list — ${gap}% cheaper than the runner-up`
        : 'Lowest ₹/sqft in your list',
    });
  }
  if (row.priceNum != null && row.priceNum === ctx.minPrice) {
    s += 15;
    notes.push({ icon: 'money', text: `Lowest total ticket price (${row.p.price})` });
  }
  if (row.areaSqft != null && row.areaSqft === ctx.maxArea) {
    s += 15;
    notes.push({ icon: 'size', text: `Largest plot in your list (${row.p.area})` });
  }
  if (row.ht === 'clear') { s += 20; notes.push({ icon: 'safe', text: 'HT-wire clear — safe to build immediately' }); }
  else if (row.ht === 'info') { s += 10; notes.push({ icon: 'safe', text: 'HT-wires nearby but not overhead' }); }
  const amenities = Array.isArray(row.p.amenities) ? row.p.amenities : [];
  if (amenities.length > 0) {
    s += Math.min(amenities.length * 3, 20);
    notes.push({
      icon: 'amenity',
      text: `${amenities.length} premium ${amenities.length === 1 ? 'amenity' : 'amenities'}: ${amenities.slice(0, 3).join(', ')}${amenities.length > 3 ? '…' : ''}`,
    });
  }
  const deal = (row.p.deal_flag || '').toUpperCase();
  if (deal === 'BUMPER') { s += 15; notes.push({ icon: 'fire', text: '🔥 BUMPER deal — limited-time price crash' }); }
  else if (deal === 'HOT') { s += 8; notes.push({ icon: 'fire', text: '🔥 HOT deal listing' }); }
  if (row.p.frontage_ft) {
    s += 5;
    notes.push({ icon: 'road', text: `${row.p.frontage_ft} ft road frontage` });
  }
  return { score: s, notes };
};

const NOTE_ICONS = {
  value:   <TrendingUp size={12} className="text-emerald-600" />,
  money:   <TrendingUp size={12} className="text-emerald-600" />,
  size:    <Maximize2  size={12} className="text-sky-600" />,
  safe:    <Zap        size={12} className="text-lime-600" />,
  amenity: <Award      size={12} className="text-violet-600" />,
  fire:    <Zap        size={12} className="text-amber-600" />,
  road:    <MapPin     size={12} className="text-slate-600" />,
};

const formatCr = (n) => {
  if (!n || !Number.isFinite(n)) return '—';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000)     return `₹${(n / 1000).toFixed(0)} K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};

const BestPickCard = ({ saved, htStatus }) => {
  const rows = saved.map(p => ({
    p,
    priceNum: parsePriceToRupees(p.price),
    areaSqft: parseAreaToSqft(p.area),
    perSqft: pricePerSqft(p.price, p.area),
    ht: htStatus[p.id] || 'unknown',
  }));

  const finite = (xs) => xs.filter(x => Number.isFinite(x));
  const minOr = (xs) => (finite(xs).length ? Math.min(...finite(xs)) : null);
  const maxOr = (xs) => (finite(xs).length ? Math.max(...finite(xs)) : null);

  const minPrice   = minOr(rows.map(r => r.priceNum));
  const minPerSqft = minOr(rows.map(r => r.perSqft));
  const maxArea    = maxOr(rows.map(r => r.areaSqft));
  const sortedPerSqft = finite(rows.map(r => r.perSqft)).sort((a, b) => a - b);
  const runnerUpPerSqft = sortedPerSqft[1] || null;

  const ctx = { minPrice, minPerSqft, maxArea, runnerUpPerSqft };

  const scored = rows.map(r => ({ ...r, ...buildScore(r, ctx) }));
  scored.sort((a, b) => b.score - a.score);
  const winner = scored[0];
  if (!winner || winner.notes.length === 0) return null;

  // 10-year projection at 9% CAGR
  const price = winner.priceNum;
  const futureValue = price ? price * ((1 + CAGR) ** HORIZON_YEARS) : null;
  const profit = price && futureValue ? futureValue - price : null;
  const multiplier = ((1 + CAGR) ** HORIZON_YEARS).toFixed(2);

  const img = winner.p.image?.match(/^\/api\/(uploads|files)\//)
    ? `${BACKEND_URL}${winner.p.image}`
    : winner.p.image;

  return (
    <div className="am-best-pick" data-testid="best-pick-card">
      <div className="am-best-pick-badge">
        <Trophy size={12} /> BEST PICK FROM YOUR SHORTLIST
      </div>
      <div className="am-best-pick-grid">
        <div className="am-best-pick-media">
          <img
            src={img}
            alt={winner.p.title}
            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=800&q=80'; }}
          />
          <div className="am-best-pick-score">Score {winner.score}/100</div>
        </div>
        <div className="am-best-pick-body">
          <h4 className="am-best-pick-title">{winner.p.title}</h4>
          <div className="am-best-pick-loc">
            <MapPin size={12} /> {winner.p.location}
          </div>
          <div className="am-best-pick-priceline">
            <span className="am-best-pick-price">{winner.p.price}</span>
            <span className="am-best-pick-area">{winner.p.area}</span>
          </div>

          <div className="am-best-pick-reasons">
            <div className="am-best-pick-reasons-title">Why this is your best buy</div>
            <ul>
              {winner.notes.slice(0, 3).map((n, i) => (
                <li key={i} data-testid={`best-pick-reason-${i}`}>
                  {NOTE_ICONS[n.icon] || <CheckCircle2 size={12} className="text-slate-500" />} <span>{n.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {price && (
            <div className="am-best-pick-projection" data-testid="best-pick-projection">
              <div className="am-best-pick-proj-title">
                <TrendingUp size={13} /> Your money in 10 years
              </div>
              <div className="am-best-pick-proj-grid">
                <div>
                  <div className="am-best-pick-proj-label">You pay today</div>
                  <div className="am-best-pick-proj-val">{formatCr(price)}</div>
                </div>
                <div>
                  <div className="am-best-pick-proj-label">Value in 2036</div>
                  <div className="am-best-pick-proj-val am-best-pick-proj-future">{formatCr(futureValue)}</div>
                </div>
                <div>
                  <div className="am-best-pick-proj-label">Estimated profit</div>
                  <div className="am-best-pick-proj-val am-best-pick-proj-profit">+ {formatCr(profit)}</div>
                </div>
              </div>
              <div className="am-best-pick-proj-note">
                Projection uses <b>9% CAGR</b> — the historical average for residential
                land in Kolkata suburbs over the last decade. Your plot grows <b>{multiplier}x</b>
                {' '}in 10 years. Not a guaranteed return — market conditions vary.
              </div>
            </div>
          )}

          {winner.p.property_code && (
            <a
              href={`/property/${winner.p.property_code}`}
              className="am-best-pick-cta"
              data-testid="best-pick-view-details"
            >
              View full details →
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
